import { Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { TOUR_STEPS, TourStep } from './tour-steps';

/**
 * What the tour reads of Studio chat (pages/studio/studio-chat.component), which registers itself
 * while it is on screen. The tour drives nothing but the message box and New test: the tester
 * presses every button.
 */
export interface TourStudio {
  readonly site: string | null;
  readonly busy: string | null;
  readonly authProfileId: string | null;
  readonly steps: { status?: string | null }[];
  readonly chat: { role: string; text: string; kind?: string; asked?: boolean }[];
  readonly testOpen: boolean;
  message: string;
  newTest(): void;
}

const SEEN_KEY = 'optimus-web-tour-seen';

/**
 * The console's tour, as in the desktop app (wd-desktop/app/ui/guide.js): first the console's
 * main parts, each page shown as it is explained, then — for people who can build tests — a real
 * test built in Studio chat on our demo HR site, with a note pinned to the one thing to do next.
 * Offered on the Dashboard until it is taken or turned down in this browser, and from the
 * account menu.
 */
@Injectable({ providedIn: 'root' })
export class TourService {
  /** The steps of this run of the tour, and the one showing (-1: no tour). */
  readonly steps = signal<TourStep[]>([]);
  readonly at = signal(-1);
  /** A step is getting its page ready. */
  readonly entering = signal(false);
  readonly seen = signal(this.readSeen());

  studio: TourStudio | null = null;

  constructor(private router: Router, private zone: NgZone, private auth: AuthService, private snack: MatSnackBar) {}

  get running() { return this.at() >= 0; }

  async start() {
    if (this.studio?.busy) {
      this.snack.open('Wait until Optimus has finished with this test', 'Close', { duration: 10000 });
      return;
    }
    this.markSeen();
    const canWrite = await firstValueFrom(this.auth.canWrite$).catch(() => false);
    const isAdmin = await firstValueFrom(this.auth.isAdmin$).catch(() => false);
    this.steps.set(TOUR_STEPS.filter((s) => (s.part === 'console' || canWrite) && (!s.adminOnly || isAdmin)));
    await this.go(0);
  }

  /** The console's part has Back and Next; the test's part moves on when Studio shows it is done. */
  next() { this.go(this.at() + 1); }
  back() { if (this.at() > 0) { this.go(this.at() - 1); } }

  end(finished: boolean) {
    this.at.set(-1);
    document.querySelector('.guide-target')?.classList.remove('guide-target');
    if (finished) {
      const built = this.steps().some((s) => s.part === 'test');
      this.snack.open(built ? 'That\'s the tour. Type your own site\'s address in Studio chat to build a real test.'
        : 'That\'s the tour. Take it again any time from your account menu.', 'Close', { duration: 10000 });
    }
  }

  markSeen() {
    this.seen.set(true);
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* it is offered again next time */ }
  }

  /** The page a step explains, and Studio chat once it is on screen. */
  async show(path: string) {
    if (!this.router.url.startsWith(path)) { await this.zone.run(() => this.router.navigateByUrl(path)); }
  }

  async openStudio(): Promise<TourStudio | null> {
    await this.show('/admin/studio-chat');
    for (let i = 0; i < 60 && !this.studio; i++) { await new Promise((r) => setTimeout(r, 50)); }
    return this.studio;
  }

  private async go(n: number) {
    const step = this.steps()[n];
    if (!step) { this.end(true); return; }
    this.at.set(n);
    this.entering.set(true);
    try { await this.zone.run(async () => step.enter?.(this)); } finally { this.entering.set(false); }
  }

  private readSeen(): boolean {
    try { return !!localStorage.getItem(SEEN_KEY); } catch { return true; }
  }
}
